import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { useTranslate } from "react-admin";
import InfoIcon from "@material-ui/icons/Info";
import { formatTooltipAxis } from "./chartUtils";
import { useFuelEconomyByAction } from "../hooks/useFuelEconomyHooks";
import type { FuelEconomyHookParams } from "../hooks/useFuelEconomyHooks";
import { useProLogsTheme } from "../../../common/hooks/useProLogsTheme";
import ReactECharts from "echarts-for-react";
import { differenceInDays, subDays, startOfDay, format } from "date-fns";
import "./FuelEconomyReport.css";

type Props = FuelEconomyHookParams;

export const FuelEconomyByActionWidget: React.FC<Props> = ({
  fromDate,
  toDate,
  periodType,
}) => {
  const translate = useTranslate();
  const theme = useProLogsTheme();
  const { data, loading: isLoading, error } = useFuelEconomyByAction({
    fromDate,
    toDate,
    periodType,
  });
  const isError = !!error;

  const chartOption = useMemo(() => {
    const actions = data?.actions ?? [];

    const categories = actions.map((action) => {
      const actionKey = action.action?.toLowerCase().replace(/\s+/g, "") ?? "";
      return translate(`fuelEconomyReport.action.${actionKey}`, {
        _: action.action ?? "",
      });
    });

    const currentPeriodData = actions.map(
      (action) => action.currentPeriod ?? 0,
    );
    const previousPeriodData = actions.map(
      (action) => action.previousPeriod ?? 0,
    );

    const currentFrom = startOfDay(fromDate);
    const currentTo = startOfDay(toDate);
    const periodDuration = differenceInDays(currentTo, currentFrom) + 1;
    const previousPeriodEnd = subDays(currentFrom, 1);
    const previousPeriodStart = subDays(previousPeriodEnd, periodDuration - 1);

    const currentPeriodLabel = `${format(currentFrom, "MMM d")} - ${format(
      currentTo,
      "MMM d, yyyy",
    )}`;
    const previousPeriodLabel = `${format(
      previousPeriodStart,
      "MMM d",
    )} - ${format(previousPeriodEnd, "MMM d, yyyy")}`;

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        formatter: formatTooltipAxis,
      },
      legend: {
        data: [
          `${translate("fuelEconomyReport.currentPeriod", {
            _: "Current Period",
          })} (${currentPeriodLabel})`,
          `${translate("fuelEconomyReport.previousPeriod", {
            _: "Previous Period",
          })} (${previousPeriodLabel})`,
        ],
        textStyle: {
          color: theme.palette.text.primary,
        },
      },
      grid: {
        left: "5%",
        right: "4%",
        bottom: "3%",
        top: "10%",
        containLabel: true,
      },
      xAxis: {
        type: "value",
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      yAxis: {
        type: "category",
        data: categories,
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          color: theme.palette.text.primary,
        },
      },
      series: [
        {
          name: `${translate("fuelEconomyReport.currentPeriod", {
            _: "Current Period",
          })} (${currentPeriodLabel})`,
          type: "bar",
          data: currentPeriodData,
          itemStyle: {
            color: "#3b82f6", // Blue
          },
        },
        {
          name: `${translate("fuelEconomyReport.previousPeriod", {
            _: "Previous Period",
          })} (${previousPeriodLabel})`,
          type: "bar",
          data: previousPeriodData,
          itemStyle: {
            color: "#10b981", // Green
          },
        },
      ],
    };
  }, [data, translate, theme.palette.text.primary, fromDate, toDate]);

  return (
    <Card className="widget" style={{ height: "100%", width: "100%" }}>
      <div className="widget-title">
        <div className="widget-title-icon" style={{ color: "#1976d2" }}>
          <InfoIcon />
        </div>
        <div className="widget-title-label">
          {translate("fuelEconomyReport.fuelEconomyByAction", {
            _: "Fuel economy by action",
          })}
        </div>
      </div>
      <CardContent
        style={{
          padding: 16,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="error">
              {translate("common.errorLoadingData", {
                _: "Error loading data",
              })}
            </Typography>
          </Box>
        ) : !data?.actions?.length ? (
          <Box
            display="flex"
            justifyContent="center"
            style={{ flex: 1, alignItems: "center" }}
          >
            <Typography variant="body2" color="textSecondary">
              {translate("common.noData", {
                _: "No data",
              })}
            </Typography>
          </Box>
        ) : (
          <ReactECharts
            style={{ flex: 1, minHeight: 0 }}
            option={chartOption}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default FuelEconomyByActionWidget;
